import { autorun, makeAutoObservable, runInAction, set, toJS } from "mobx";
import BrowserX from "./BrowserX";
import { LandingPageSetting } from "Pages/UserControl/SysRole/Component/RoleInfo";
import { AvailLANG } from "interfaces";

class AppStore {
  browser = "";
  user = {
    authority: null,
    level: null,
    JWT: "",
    Username: null,
    UserDisplayName: null,
    Instance: null,
    landingPage: LandingPageSetting.StarterDashboard,
    columnVisibilitySetting: {},
    filterSetting: {},
  };
  server = {
    Env: null,
    backendVersion: null,
    gptQaVersion: null,
  };
  chatbot = {
    chatbotVersion: null,
  };
  alert = {};
  loading = false;
  initialized = false;
  ask = {};
  sysInfo = {
    initialized: false,
    Language: {
      default: AvailLANG.EN,
      available: [
        { display: "English", value: "EN" },
        { display: "中文 （䌓)", value: "TC" },
        { display: "中文 （簡)", value: "SC" },
      ],
    },
  };
  urlParams = {
    lang: "",
  };

  chatbotLang = AvailLANG.EN;
  role = undefined;

  classicMenuMode = null;

  constructor() {
    makeAutoObservable(this);

    let firstRun = true;

    autorun(() => {
      // on load check if there's an existing store on
      // localStorage and extend the store
      if (firstRun) {
        let existingStore = window.localStorage.getItem("store");

        if (existingStore) {
          set(this, JSON.parse(existingStore));
        }
        this.role = undefined;
      }

      // from then on serialize and save to localStorage
      let serializedThis = toJS(this);
      window.localStorage.setItem("store", JSON.stringify(serializedThis));
    });

    firstRun = false;
  }

  setRole(role) {
    runInAction(() => {
      if (!role) {
        role = null;
      }
      this.role = role;
    });
  }

  setLang(lang) {
    runInAction(() => {
      this.chatbotLang = lang;
    });
  }

  setBrowser() {
    runInAction(() => {
      let browser = BrowserX.getBrowser();
      this.browser = browser;
    });
  }

  setUser(user) {
    runInAction(() => {
      this.user = user;
    });
  }

  clearUser() {
    runInAction(() => {
      this.user = {};
    });
  }

  setServer(server) {
    runInAction(() => {
      this.server = server;
    });
  }

  setChatbot(payload) {
    runInAction(() => {
      this.chatbot = payload;
    });
  }

  setUrlParams(urlParams) {
    runInAction(() => {
      this.urlParams = urlParams;
    });
  }

  Alert(message, severity = "info") {
    runInAction(() => {
      this.alert = {
        message,
        severity,
      };
    });
  }

  Ask(title, message = "", onConfirm, onCancel, autoClose = true, buttons = ["OK", "Cancel"]) {
    runInAction(() => {
      this.ask = {
        title,
        message,
        onConfirm,
        onCancel,
        buttons: buttons,
        showCloseIcon: false,
        loading: false,
        autoClose: autoClose,
      };
    });
  }

  SetAskLoading(f) {
    runInAction(() => {
      this.ask.loading = f;
    });
  }

  Form(title, message = "", inner = null, onConfirm = null, onCancel = null) {
    runInAction(() => {
      this.ask = {
        title,
        message,
        onConfirm,
        onCancel,
        inner,
        buttons: [],
        showCloseIcon: true,
      };
    });
  }

  Pop(title, message = "", onConfirm = null) {
    runInAction(() => {
      this.ask = {
        title,
        message,
        onConfirm,
        buttons: ["OK"],
      };
    });
  }

  clearAlert() {
    runInAction(() => {
      this.alert = {};
    });
  }

  clearAsk() {
    runInAction(() => {
      this.ask = {};
    });
  }

  isLoggedIn() {
    return this.user.JWT;
  }

  isLoading(f) {
    runInAction(() => {
      this.loading = f;
    });
  }

  setInitialized(f) {
    runInAction(() => {
      this.initialized = f;
    });
  }

  isInitialized() {
    return this.initialized;
  }

  setSysInfo(payload) {
    runInAction(() => {
      this.sysInfo.initialized = true;
      this.sysInfo = { ...this.sysInfo, ...payload };
    });
  }

  setClassicMenuMode(mode) {
    runInAction(() => {
      this.classicMenuMode = mode;
    });
  }

  clearClassicMenuMode() {
    runInAction(() => {
      this.classicMenuMode = null;
    });
  }

  getFilterSetting(page) {
    if (!this.user.filterSetting) return {};
    let stringifiedFS = JSON.stringify(this.user.filterSetting);
    let jsonFS = JSON.parse(stringifiedFS);
    return jsonFS ? jsonFS[page] : {};
  }

  setFilterSettingFilter(newSetting, page) {
    runInAction(() => {
      this.user.filterSetting[page] = {
        ...this.user.filterSetting[page],
        filters: newSetting,
      };
    });
  }

  setFilterSettingDateRange(newDateRange, page) {
    let DateRange = newDateRange;
    if (Array.isArray(newDateRange) && newDateRange.length == 2) {
      DateRange = {
        from: newDateRange[0],
        to: newDateRange[1],
      };
    }
    runInAction(() => {
      this.user.filterSetting[page] = {
        ...this.user.filterSetting[page],
        DateRange,
      };
    });
  }

  getColumnVisibilitySetting(page) {
    if (!this.user.columnVisibilitySetting) return {};
    let stringifiedCVS = JSON.stringify(this.user.columnVisibilitySetting);
    let jsonCVS = JSON.parse(stringifiedCVS);
    return jsonCVS ? jsonCVS[page] : {};
  }

  setColumnVisibilitySetting(newSetting, page) {
    runInAction(() => {
      this.user.columnVisibilitySetting = {
        ...this.user.columnVisibilitySetting,
        [page]: newSetting,
      };
    });
  }
}

const store = new AppStore();
export default store;
